import React, { useEffect, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { Button } from '../common/index.js';
import { stockApi } from '../../api/stockApi';

export const formatPrice = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toLocaleString('en-IN') : value || '0';
};

// Investment recovery — the purchase price is the investment; every part sold
// out of this car chips away at it. Past break-even, everything is profit.
export const InvestmentRecovery = ({ stock }) => {
  const [pnl, setPnl] = useState(stock.pnl || null);
  const [item, setItem] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setPnl(stock.pnl || null); }, [stock.id, stock.pnl]);

  const addSale = async (e) => {
    e.preventDefault();
    setErr('');
    const amt = Number(amount);
    if (!item.trim()) { setErr('Enter what was sold'); return; }
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter a valid amount'); return; }
    setSaving(true);
    try {
      const res = await stockApi.addSale(stock.id, { item_name: item.trim(), amount: amt });
      setPnl((res.data ?? res).pnl);
      setItem(''); setAmount('');
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message || 'Could not record the sale');
    } finally {
      setSaving(false);
    }
  };

  const removeSale = async (saleId) => {
    try {
      const res = await stockApi.removeSale(stock.id, saleId);
      setPnl((res.data ?? res).pnl);
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message || 'Could not remove the sale');
    }
  };

  if (!pnl) return null;
  const recovered = Number(pnl.recovered);
  const remaining = Number(pnl.remaining);
  const profit = Number(pnl.profit);

  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-800">Investment recovery</p>
        {pnl.break_even ? (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
            PROFIT ₹{formatPrice(profit)}
          </span>
        ) : (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-700">
            ₹{formatPrice(remaining)} to recover
          </span>
        )}
      </div>

      {/* Progress towards break-even */}
      <div className="mt-3">
        <div className="flex items-baseline justify-between text-xs">
          <span className="font-semibold text-slate-500">Recovered ₹{formatPrice(recovered)}</span>
          <span className="text-slate-400">of ₹{formatPrice(stock.purchase_price)} ({pnl.progress_pct}%)</span>
        </div>
        <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full transition-all ${pnl.break_even ? 'bg-emerald-500' : 'bg-violet-500'}`}
            style={{ width: `${pnl.progress_pct}%` }}
          />
        </div>
        {Number(pnl.returned) > 0 && (
          <p className="mt-1 text-[11px] font-medium text-rose-500">
            − ₹{formatPrice(pnl.returned)} refunded on returns (already deducted)
          </p>
        )}
      </div>

      {/* Sales out of this car — dispatched CRM sales land here automatically */}
      {pnl.sales?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {pnl.sales.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 truncate text-xs font-semibold text-slate-700">
                  <span className="truncate">{s.item_name}</span>
                  {s.auto && (
                    <span
                      className="shrink-0 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-violet-600"
                      title={`Recorded automatically when Inquiry #${s.inquiry_id} was dispatched`}
                    >
                      Auto
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-slate-400">{s.sold_at_display}{s.note ? ` · ${s.note}` : ''}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-sm font-bold text-slate-800">₹{formatPrice(s.amount)}</span>
                {!s.auto && (
                  <button
                    type="button"
                    onClick={() => removeSale(s.id)}
                    className="rounded-lg p-1.5 text-slate-300 hover:bg-rose-50 hover:text-rose-500"
                    title="Remove"
                  >
                    <FiTrash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Record a sale from this car */}
      <form onSubmit={addSale} className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={item}
          onChange={(e) => setItem(e.target.value)}
          placeholder="Part sold — e.g. Engine"
          disabled={saving}
          className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
        />
        <div className="relative w-28">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
          <input
            type="number"
            min="0.01"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount"
            disabled={saving}
            className="w-full rounded-xl border border-slate-200 py-2 pl-6 pr-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
        <Button type="submit" size="sm" variant="primary" loading={saving} className="shrink-0 gap-1">
          <FiPlus className="h-3.5 w-3.5" /> Add
        </Button>
      </form>
      {err && <p className="mt-1.5 text-xs font-medium text-rose-500">{err}</p>}
    </div>
  );
};

export default InvestmentRecovery;
